import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Info } from "@phosphor-icons/react";
import API from "@/lib/api";

// Human-readable labels for the eight PII fields eligible for cross-case
// inheritance (matches services/client_inheritance.PERSON_FIELDS).
const FIELD_LABELS = {
  phone: "Phone",
  address: "Address",
  dob: "Date of birth",
  ni_number: "NI number",
  id_type: "ID type",
  id_number: "ID number",
  id_expiry: "ID expiry",
  marital_status: "Marital status",
};

const FIELD_ORDER = [
  "phone", "address", "dob", "ni_number",
  "id_type", "id_number", "id_expiry", "marital_status",
];

/**
 * ReviewInheritanceDialog — side-by-side "Source vs New" panel that lets
 * a solicitor accept (per-field) or reject inherited PII from a client's
 * prior case. Replaces the old silent auto-copy path.
 *
 * Props:
 *   open           — controlled boolean
 *   onOpenChange   — setter
 *   caseId         — the CURRENT case id (where the client sits)
 *   clientKey      — real user id OR lowercase email (server matches on either)
 *   clientEntry    — the full client dict from caseData.clients[]
 *   onReviewed     — callback(updatedCase) fired after accept/reject succeeds
 */
export default function ReviewInheritanceDialog({
  open, onOpenChange, caseId, clientKey, clientEntry, onReviewed,
}) {
  const pending = clientEntry?.pending_inheritance || {};
  const candidates = pending.candidate_fields || {};
  const availableFields = useMemo(
    () => FIELD_ORDER.filter((f) => candidates[f]),
    [candidates],
  );
  // Default: every candidate is selected (mirrors old auto-copy behaviour
  // but gated behind a confirm click so nothing lands without consent).
  const [selected, setSelected] = useState(() => new Set(availableFields));
  const [busy, setBusy] = useState(false);

  const toggle = (f) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
  };

  const allChecked = availableFields.length > 0 && availableFields.every((f) => selected.has(f));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(availableFields));
  };

  const submit = async (action) => {
    setBusy(true);
    try {
      const path = action === "accept"
        ? `/cases/${caseId}/clients/${encodeURIComponent(clientKey)}/accept-inheritance`
        : `/cases/${caseId}/clients/${encodeURIComponent(clientKey)}/reject-inheritance`;
      const body = action === "accept" ? { fields: Array.from(selected) } : {};
      const { data } = await API.post(path, body);
      onReviewed?.(data.case);
      onOpenChange?.(false);
      if (action === "accept") {
        const n = data.applied_fields?.length || 0;
        toast.success(n > 0 ? `Applied ${n} field${n === 1 ? "" : "s"} from prior case` : "Review complete — no fields copied");
      } else {
        toast.success("Inheritance rejected — no fields copied");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        data-testid="review-inheritance-dialog"
      >
        <DialogHeader>
          <DialogTitle>Review PII from prior case</DialogTitle>
          <DialogDescription className="sr-only">
            Side-by-side comparison of the client's details on this case versus their most recent prior case. Tick the fields to copy across; nothing is written until you click Accept selected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex gap-2 items-start">
            <Info weight="fill" className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">
                {clientEntry?.name || clientEntry?.email} appeared on a previous case.
              </p>
              <p className="text-amber-800/90 mt-0.5">
                Source: <span className="font-medium">{pending.source_case_title || "(untitled case)"}</span>
                {pending.source_updated_at && (
                  <span className="text-amber-700/70"> · last updated {new Date(pending.source_updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                )}
              </p>
              <p className="text-amber-800/80 mt-1">
                Tick the fields you want copied onto this case. Nothing has been written yet.
              </p>
            </div>
          </div>

          {availableFields.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              No fields available to review — you can dismiss this panel.
            </p>
          ) : (
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="grid grid-cols-[auto_1fr_1fr] items-center gap-3 bg-slate-50 border-b border-slate-200 px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500 font-medium">
                <button
                  type="button"
                  className="text-slate-600 hover:text-slate-900"
                  onClick={toggleAll}
                  data-testid="review-inheritance-toggle-all"
                >
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={toggleAll}
                    aria-label="Toggle all"
                  />
                </button>
                <div>Current on this case</div>
                <div>From prior case</div>
              </div>
              <div className="divide-y divide-slate-100">
                {availableFields.map((f) => {
                  const cur = clientEntry?.[f];
                  return (
                    <div
                      key={f}
                      className="grid grid-cols-[auto_1fr_1fr] items-start gap-3 px-3 py-2.5"
                      data-testid={`review-inheritance-row-${f}`}
                    >
                      <div className="pt-0.5">
                        <Checkbox
                          checked={selected.has(f)}
                          onCheckedChange={() => toggle(f)}
                          data-testid={`review-inheritance-check-${f}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500 tracking-wide">
                          {FIELD_LABELS[f] || f}
                        </Label>
                        <p className="text-xs text-slate-700 break-words">
                          {cur || <span className="text-slate-400 italic">Blank</span>}
                        </p>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-amber-600 tracking-wide">
                          Candidate
                        </Label>
                        <p className="text-xs text-amber-900 font-medium break-words">
                          {candidates[f]}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2">
          <Button
            variant="outline"
            onClick={() => submit("reject")}
            disabled={busy}
            data-testid="review-inheritance-reject-btn"
          >
            Reject all
          </Button>
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={busy}
              data-testid="review-inheritance-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => submit("accept")}
              disabled={busy || availableFields.length === 0}
              data-testid="review-inheritance-accept-btn"
            >
              Accept selected ({selected.size})
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
